import { useEffect, useRef } from "react";

import { parseRoomPermalink, type OpenRoomLink, type RoomPermalink } from "~/lib/permalink";

const PREFIX = "#/room/";

const LAUNCH_HASH_KEY = "nurion-chat:launchHash";

/** The room, and optionally event, a `#/room/<roomId>[/<eventId>]` launch URL opens. */
export function parseLaunchHash(hash: string): RoomPermalink | null {
  if (!hash.startsWith(PREFIX)) return null;
  return parseRoomPermalink(`https://matrix.to/#/${hash.slice(PREFIX.length)}`);
}

export function rememberLaunchHash(): void {
  try {
    if (parseLaunchHash(window.location.hash) === null) return;
    sessionStorage.setItem(LAUNCH_HASH_KEY, window.location.hash);
  } catch {
    return;
  }
}

export function takeRememberedLaunchHash(): string {
  try {
    const value = sessionStorage.getItem(LAUNCH_HASH_KEY);
    if (value == null) return "";
    sessionStorage.removeItem(LAUNCH_HASH_KEY);
    return parseLaunchHash(value) === null ? "" : value;
  } catch {
    return "";
  }
}

/** Opens the launch URL's room once the shell has its rooms, then drops the hash. */
export function useLaunchRoomLink(openRoomLink: OpenRoomLink, ready: boolean): void {
  const done = useRef(false);
  useEffect(() => {
    if (done.current || !ready) return;
    done.current = true;
    const link = parseLaunchHash(window.location.hash);
    if (!link) return;
    openRoomLink(link);
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
  }, [openRoomLink, ready]);
}
